import { SupportedLanguage } from '../config/languageConfig';
import { POPUP_SETTINGS_GUIDE_URL } from '../config/constants';

export interface FAQItem {
  id: string;
  question: Record<SupportedLanguage, string>;
  answer: Record<SupportedLanguage, string>;
  answerLink?: {
    href: string;
    label: string;
  };
}

export interface FAQSection {
  id: string;
  title: string;
  items: FAQItem[];
}

// FAQ 섹션 데이터 (실제 질문/답변)
export const faqSections: FAQSection[] = [
  {
    id: 'service-overview',
    title: 'faq.section.serviceOverview',
    items: [
      {
        id: 'service-1',
        question: {
          en: 'What does this service do?',
          ko: 'What does this service do?',
          ja: 'このサービスは何をしてくれるの？',
          'zh-CN': '这项服务的功能是什么？',
          'zh-TW': '這項服務的功能是什麼？'
        },
        answer: {
          en: 'We help you order food from Korean restaurants on your behalf. Just tell us what you want in English — we\'ll place the order in Korean and get it delivered to you.',
          ko: 'We help you order food from Korean restaurants on your behalf. Just tell us what you want in English — we\'ll place the order in Korean and get it delivered to you.',
          ja: '韓国のレストランに代わって注文を行うサービスです。日本語で希望の料理を伝えていただければ、私たちが韓国語で注文し、配達までサポートします。',
          'zh-CN': '我们代替您向韩国餐厅订餐。您只需用中文告诉我们想吃什么，我们会用韩文下单并安排送达。',
          'zh-TW': '我們代替您向韓國餐廳訂餐。您只需用中文告訴我們想吃什麼，我們會用韓文下單並安排送達。'
        }
      },
      {
        id: 'service-2',
        question: {
          en: 'Can I order from any restaurant in Korea?',
          ko: 'Can I order from any restaurant in Korea?',
          ja: 'どのレストランからでも注文できますか？',
          'zh-CN': '所有韩国餐厅都可以订餐吗？',
          'zh-TW': '所有韓國餐廳都可以訂餐嗎？'
        },
        answer: {
          en: 'Most delivery-available restaurants are supported. Some may be restricted depending on the area.',
          ko: 'Most delivery-available restaurants are supported. Some may be restricted depending on the area.',
          ja: 'ほとんどのデリバリー対応店舗は可能ですが、一部の地域では制限があります。',
          'zh-CN': '大部分可外送的餐厅皆可订餐，但部分餐厅因地区限制可能无法提供服务。',
          'zh-TW': '大部分可外送的餐廳皆可訂餐，但部分餐廳因地區限制可能無法提供服務。'
        }
      }
    ]
  },
  {
    id: 'order-payment',
    title: 'faq.section.orderPayment',
    items: [
      {
        id: 'order-1',
        question: {
          en: 'How do I place an order?',
          ko: 'How do I place an order?',
          ja: '注文の方法を教えてください。',
          'zh-CN': '如何下订单？',
          'zh-TW': '如何下訂單？'
        },
        answer: {
          en: 'Simply select your menu after accessing our service, then complete the payment using your available card.',
          ko: 'Simply select your menu after accessing our service, then complete the payment using your available card.',
          ja: 'サービスにアクセス後、お好きなメニューを選び、お持ちのカードでお支払いください。',
          'zh-CN': '进入本服务后，请选择您想要的菜单，并使用可用的银行卡完成付款。',
          'zh-TW': '進入本服務後，請選擇您想要的餐點，並使用您的可用信用卡完成付款。'
        }
      },
      {
        id: 'order-2',
        question: {
          en: 'What payment methods are supported?',
          ko: 'What payment methods are supported?',
          ja: '支払い方法は？',
          'zh-CN': '支持哪些付款方式？',
          'zh-TW': '支援哪些付款方式？'
        },
        answer: {
          en: 'We accept international cards and PayPal when available. Alipay+ and WeChat Pay are available through Eximbay on the checkout page.',
          ko: '해외 카드와 PayPal(이용 가능한 경우)을 지원합니다. Alipay+와 WeChat Pay는 결제 화면에서 엑심베이를 선택해 이용할 수 있습니다.',
          ja: '海外発行のカードと、利用可能な場合はPayPalをご利用いただけます。Alipay+とWeChat Payは、決済画面でEximbayを選択してご利用ください。',
          'zh-CN': '我们支持国际银行卡，以及当前可用时的 PayPal。Alipay+ 和 WeChat Pay 可在结账页面通过 Eximbay 使用。',
          'zh-TW': '我們支援國際信用卡，以及目前可用時的 PayPal。Alipay+ 與 WeChat Pay 可在結帳頁面透過 Eximbay 使用。'
        }
      },
      {
        id: 'order-4',
        question: {
          en: 'What should I do if the payment window does not open?',
          ko: '결제창이 열리지 않으면 어떻게 하나요?',
          ja: '決済画面が開かない場合はどうすればよいですか？',
          'zh-CN': '如果支付窗口没有打开，该怎么办？',
          'zh-TW': '如果付款視窗沒有開啟，該怎麼辦？'
        },
        answer: {
          en: 'Check your browser’s pop-up settings. The guide below shows how to allow the payment window.',
          ko: '브라우저의 팝업 설정을 확인해 주세요. 아래 가이드에서 결제창을 허용하는 방법을 볼 수 있습니다.',
          ja: 'ブラウザのポップアップ設定をご確認ください。下のガイドで決済画面を許可する方法をご覧いただけます。',
          'zh-CN': '请检查浏览器的弹窗设置。您可以在下方指南中查看如何允许支付窗口。',
          'zh-TW': '請檢查瀏覽器的彈出視窗設定。您可以在下方指南中查看如何允許付款視窗。'
        },
        answerLink: {
          href: POPUP_SETTINGS_GUIDE_URL,
          label: 'checkout.popupGuideLink'
        }
      },
      {
        id: 'order-3',
        question: {
          en: 'Can I change the payment method after ordering?',
          ko: 'Can I change the payment method after ordering?',
          ja: '注文後に支払い方法を変更できますか？',
          'zh-CN': '订单完成后可以更改付款方式吗？',
          'zh-TW': '訂單完成後可以更改付款方式嗎？'
        },
        answer: {
          en: 'Payment method cannot be changed after the order is placed. Please decide before confirming your order.',
          ko: 'Payment method cannot be changed after the order is placed. Please decide before confirming your order.',
          ja: '注文確定後は支払い方法の変更はできません。ご注文前にご確認ください。',
          'zh-CN': '订单成立后无法变更付款方式，请于下单前确认好您的支付方式。',
          'zh-TW': '訂單成立後無法變更付款方式，請於下單前確認好您的支付方式。'
        }
      }
    ]
  },
  {
    id: 'delivery-time',
    title: 'faq.section.deliveryTime',
    items: [
      {
        id: 'delivery-1',
        question: {
          en: 'How long does delivery usually take?',
          ko: 'How long does delivery usually take?',
          ja: '配達にはどのくらい時間がかかりますか？',
          'zh-CN': '通常需要多久送达？',
          'zh-TW': '通常需要多久送達？'
        },
        answer: {
          en: 'It usually takes 40 to 60 minutes, but it may vary depending on the restaurant, weather, and distance.',
          ko: 'It usually takes 40 to 60 minutes, but it may vary depending on the restaurant, weather, and distance.',
          ja: '通常は40〜60分程度ですが、お店の状況や天候、距離によって異なる場合があります。',
          'zh-CN': '约需 40～60 分钟，实际时间可能因餐厅、天气与距离有所不同。',
          'zh-TW': '約需 40～60 分鐘，實際時間可能因餐廳、天氣與距離有所不同。'
        }
      },
      {
        id: 'delivery-2',
        question: {
          en: 'How can I check the delivery status?',
          ko: 'How can I check the delivery status?',
          ja: '配達の進行状況はどうやって確認できますか？',
          'zh-CN': '如何查看配送进度？',
          'zh-TW': '如何查看配送進度？'
        },
        answer: {
          en: 'We\'ll send real-time updates to the email you provided during the order.',
          ko: 'We\'ll send real-time updates to the email you provided during the order.',
          ja: 'ご注文時に入力されたメールアドレス宛に、配達状況をリアルタイムでお送りします。',
          'zh-CN': '我们会将配送状况发送到您下单时填写的电子信箱。',
          'zh-TW': '我們會將配送狀況發送到您下單時填寫的電子信箱。'
        }
      },
      {
        id: 'delivery-3',
        question: {
          en: 'How do I know if delivery is available to my location?',
          ko: 'How do I know if delivery is available to my location?',
          ja: '配達できる地域かどうかはどうやって分かりますか？',
          'zh-CN': '如何知道我的地点是否可以配送？',
          'zh-TW': '如何知道我的地點是否可以配送？'
        },
        answer: {
          en: 'After entering your address, only restaurants available for delivery will be shown.',
          ko: 'After entering your address, only restaurants available for delivery will be shown.',
          ja: '住所を入力すると、配達可能な店舗のみが自動的に表示されます。',
          'zh-CN': '输入地址后，系统只会显示可以配送的餐厅。',
          'zh-TW': '輸入地址後，系統只會顯示可以配送的餐廳。'
        }
      }
    ]
  },
  {
    id: 'cancel-issues',
    title: 'faq.section.cancelIssues',
    items: [
      {
        id: 'cancel-1',
        question: {
          en: 'Can I cancel my order?',
          ko: 'Can I cancel my order?',
          ja: '注文をキャンセルできますか？',
          'zh-CN': '我可以取消订单吗？',
          'zh-TW': '我可以取消訂單嗎？'
        },
        answer: {
          en: 'Once payment is completed, the order cannot be canceled. Cancellation is only possible in rare cases such as restaurant issues or natural disasters.',
          ko: 'Once payment is completed, the order cannot be canceled. Cancellation is only possible in rare cases such as restaurant issues or natural disasters.',
          ja: 'お支払い完了後の注文キャンセルはできません。ただし、店舗都合や天災などの場合に限り対応可能です。',
          'zh-CN': '付款完成后无法取消订单，仅限于餐厅营业问题或天灾等特殊情况下才能取消。',
          'zh-TW': '付款完成後無法取消訂單，僅限於餐廳營業問題或天災等特殊情況下才能取消。'
        }
      },
      {
        id: 'cancel-2',
        question: {
          en: 'I received the wrong food. What should I do?',
          ko: 'I received the wrong food. What should I do?',
          ja: '届いた商品が違っていました。どうすればいいですか？',
          'zh-CN': '食物送错了，该怎么办？',
          'zh-TW': '食物送錯了，該怎麼辦？'
        },
        answer: {
          en: 'Please send us a photo via chat. We\'ll check with the restaurant and assist with the resolution.',
          ko: 'Please send us a photo via chat. We\'ll check with the restaurant and assist with the resolution.',
          ja: 'チャットで写真を送ってください。お店に確認のうえ、対応を進めます。',
          'zh-CN': '请透过聊天室传送照片，我们会联系餐厅并协助处理。',
          'zh-TW': '請透過聊天室傳送照片，我們會聯繫餐廳並協助處理。'
        }
      },
      {
        id: 'cancel-3',
        question: {
          en: 'The delivery is taking too long.',
          ko: 'The delivery is taking too long.',
          ja: '配達が遅すぎます。',
          'zh-CN': '食物送太久了怎么办？',
          'zh-TW': '食物送太久了怎麼辦？'
        },
        answer: {
          en: 'If the delivery is delayed over 90 minutes, we\'ll check the situation in real time and help resolve it.',
          ko: 'If the delivery is delayed over 90 minutes, we\'ll check the situation in real time and help resolve it.',
          ja: '配達が90分以上遅れている場合は、状況を確認し対応いたします。',
          'zh-CN': '若配送延迟超过90分钟，我们会即时确认状况并协助处理。',
          'zh-TW': '若配送延遲超過90分鐘，我們會即時確認狀況並協助處理。'
        }
      }
    ]
  },
  {
    id: 'language-others',
    title: 'faq.section.languageOthers',
    items: [
      {
        id: 'language-1',
        question: {
          en: 'Can I write allergy notes or requests in English?',
          ko: 'Can I write allergy notes or requests in English?',
          ja: 'アレルギーや要望は日本語で書いても大丈夫ですか？',
          'zh-CN': '我可以用英文写过敏或特殊需求吗？',
          'zh-TW': '我可以用英文寫過敏或特殊需求嗎？'
        },
        answer: {
          en: 'Yes, we\'ll translate your English request into Korean and send it to the restaurant. However, please note that not all requests can be fully guaranteed. In rare cases, your order may be canceled if the restaurant cannot accommodate it.',
          ko: 'Yes, we\'ll translate your English request into Korean and send it to the restaurant. However, please note that not all requests can be fully guaranteed. In rare cases, your order may be canceled if the restaurant cannot accommodate it.',
          ja: 'はい、日本語でご記入いただいた内容を韓国語に翻訳してお店に伝えます。ただし、全ての要望が確実に反映されるとは限りません。対応できない場合、注文がキャンセルされる可能性もありますのでご了承ください。',
          'zh-CN': '可以！我们会将您的需求翻译成韩文并传达给餐厅。但部分需求可能无法完全实现，若餐厅无法处理，有可能会取消订单，敬请见谅。',
          'zh-TW': '可以！我們會將您的需求翻譯成韓文並傳達給餐廳。但部分需求可能無法完全實現，若餐廳無法處理，有可能會取消訂單，敬請見諒。'
        }
      },
      {
        id: 'language-2',
        question: {
          en: 'What if the delivery rider doesn\'t speak English?',
          ko: 'What if the delivery rider doesn\'t speak English?',
          ja: '韓国語が話せなくても大丈夫ですか？',
          'zh-CN': '不会说韩文也可以使用服务吗？',
          'zh-TW': '不會說韓文也可以使用服務嗎？'
        },
        answer: {
          en: 'Most deliveries in Korea are non-contact. If needed, we\'ll assist by communicating with the rider for you. Please contact us if you need help.',
          ko: 'Most deliveries in Korea are non-contact. If needed, we\'ll assist by communicating with the rider for you. Please contact us if you need help.',
          ja: 'はい、ご安心ください！ご要望を日本語または英語で伝えていただければ、私たちがレストランや配達員とのやり取りを代行いたします。',
          'zh-CN': '当然可以！您只需用中文或英文告诉我们需求，我们会代为与餐厅与外送员沟通，让您无后顾之忧。',
          'zh-TW': '當然可以！您只需用中文或英文告訴我們需求，我們會代為與餐廳與外送員溝通，讓您無後顧之憂。'
        }
      }
    ]
  }
];
